import { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { useApolloClient, useMutation } from '@apollo/client';
import Papa from 'papaparse';

import ErrorText from '../../ErrorText';
import {
  SET_ON_CONTINUE,
  SET_LOADING,
  SET_SUCCESS,
} from '../../../reducers/actionReducer';
import uploadIcon from '../../../assets/upload-icon.svg';
import acceptedIcon from '../../../assets/accepted-icon.svg';
import rejectedIcon from '../../../assets/rejected-icon.svg';
import LoadingSpinner from '../../LoadingSpinner';
import ToastNotification from '../../ToastNotification';
import {
  COMPLETE_MULTIPART_UPLOAD,
  CREATE_MULTIPART_UPLOAD,
} from '../../../mutations';
import {
  FILE_FORMAT_HINT,
  formatBytes,
  validateParsedFile,
} from './utils';
import useNavigationPrompt from '../../../hooks/useNavigationPrompt';
import NavigationPromptModal from '../../builder/NavigationPromptModal';
import useMultipartUpload, {
  CompleteUploadArgs,
  GetPresignedUrlsArgs,
  GetPresignedUrlsResponse,
} from '../../../hooks/useMultipartUpload';
import { FILES_QUERY } from '../../../queries';
import CollapsibleInstructions from '../CollapsibleInstructions';
import { FileKind, MAX_ZIP_FILE_BYTES, validateZipPreview } from './zipCodes';

/**
 * How long an error toast stays up. Well past the 5s used for success
 * elsewhere, and pointing at the toast pauses the countdown.
 */
const ERROR_TOAST_MS = 10_000;

/**
 * ATAD-136: the toast only points at the modal. The detailed problem lives in
 * the modal's error text, which stays put; a toast clamped to three lines (and
 * on the Audience Builder, partly under the DocsBot widget) couldn't show it.
 */
export const UPLOAD_ERROR_TOAST =
  "Error. See the details in the bottom of the upload Modal.";

type UploadedFileSummary = {
  kind?: string | null;
  records?: number | null;
  numUniqueRecords?: number | null;
  numSkippedRecords?: number | null;
};

/** ATAD-136: what a finished ZIP upload tells the user — loaded and skipped. */
export function zipUploadSuccessMessage(uploaded?: UploadedFileSummary | null) {
  const loaded = uploaded?.numUniqueRecords;
  if (loaded == null) return 'ZIP code file uploaded successfully';

  const skipped = uploaded?.numSkippedRecords ?? 0;
  const plural = (n: number, word: string) =>
    `${n.toLocaleString('en-US')} ${word}${n === 1 ? '' : 's'}`;

  return (
    `ZIP code file uploaded: ${plural(loaded, 'ZIP code')} loaded` +
    (skipped > 0 ? `, ${plural(skipped, 'row')} skipped (not a valid ZIP code).` : '.')
  );
}

type FileUploadProps = {
  dispatchAction: (action: any) => void;
  /** ATAD-136: 'zip' uploads a ZIP code list used as an audience filter. */
  kind?: FileKind;
  /** Whether this modal offers the ZIP codes type (enables the switch hint). */
  zipUploadAvailable?: boolean;
};

export function FileUpload({
  dispatchAction,
  kind = 'id',
  zipUploadAvailable = false,
}: FileUploadProps) {
  const isZip = kind === 'zip';
  // The server's response to completeMultipartUpload, for the success message.
  const uploadedFileRef = useRef<UploadedFileSummary | null>(null);
  const [file, setFile] = useState<File | null>();
  const [fileError, setFileError] = useState();
  const [fileIsValid, setFileIsValid] = useState(false);
  const [fileValidationError, setFileValidationError] = useState<string>();

  const [toastShown, setToastShown] = useState(false);
  const errorTextRef = useRef<HTMLDivElement>(null);

  // Raised at each point an error is produced rather than derived from
  // `errorMessage`: choosing the same bad file twice sets the same string, so
  // a state-derived effect would go quiet exactly when the user repeated the
  // mistake and most needed telling.
  const raiseToast = useCallback(() => setToastShown(true), []);

  const dismissToast = useCallback(() => setToastShown(false), []);

  const apolloClient = useApolloClient();

  const [
    createMultipartUpload,
    {
      error: createMultipartUploadError,
      reset: resetCreateMultipartUploadMutation,
    },
  ] = useMutation(CREATE_MULTIPART_UPLOAD);
  const createMultipartUploadPromise = useCallback(
    (variables: GetPresignedUrlsArgs): Promise<GetPresignedUrlsResponse> =>
      new Promise((resolve, reject) =>
        createMultipartUpload({
          variables,
          onCompleted: (data) => {
            if (data?.createMultipartUpload) {
              resolve(data.createMultipartUpload);
            } else {
              reject();
            }
          },
          onError: (err) => {
            console.error(`Failed to create multipart upload: ${err}`);
            reject();
          },
        })
      ),
    [createMultipartUpload]
  );

  const [
    completeMultipartUpload,
    {
      error: completeMultipartUploadError,
      reset: resetCompleteMultipartUploadMutation,
    },
  ] = useMutation(COMPLETE_MULTIPART_UPLOAD);
  const completeMultipartUploadPromise = useCallback(
    (variables: CompleteUploadArgs): Promise<void> =>
      new Promise((resolve, reject) =>
        completeMultipartUpload({
          variables: { ...variables, kind },
          onCompleted: (data) => {
            if (data?.completeMultipartUpload) {
              uploadedFileRef.current = data.completeMultipartUpload;
              resolve(data.completeMultipartUpload);
            } else {
              reject();
            }
          },
          onError: (err) => {
            console.error(`Failed to complete multipart upload: ${err}`);
            reject();
          },
        })
      ),
    [completeMultipartUpload, kind]
  );

  const onDropAccepted = useCallback((acceptedFiles) => {
    setFileError(null);
    setFile(acceptedFiles[0]);
  }, []);

  const onDropRejected = useCallback(
    (fileRejections) => {
      const message = fileRejections[0]?.errors[0]?.message;
      setFileError(message);

      if (message) {
        raiseToast();
      }
    },
    [raiseToast]
  );

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
    open,
  } = useDropzone({
    onDropAccepted,
    onDropRejected,
    accept: '.csv,text/csv',
    maxFiles: 1,
  });

  const {
    upload,
    reset: resetUpload,
    state: { uploading, progress, error: uploadError, done: uploadDone },
  } = useMultipartUpload({
    getPresignedUrls: createMultipartUploadPromise,
    completeUpload: completeMultipartUploadPromise,
  });

  const error =
    createMultipartUploadError || uploadError || completeMultipartUploadError;

  // One place that answers "is something wrong, and what". `fileError` is a
  // dropzone rejection (wrong file type); until now it reached no rendered
  // element at all, so those uploads failed silently.
  const errorMessage =
    fileValidationError || fileError || error?.message || null;

  useEffect(
    function raiseToastForUploadFailure() {
      if (error?.message) {
        raiseToast();
      }
    },
    [error, raiseToast]
  );

  function handleClearFile() {
    dismissToast();
    setFile(null);
    setFileError(null);
    setFileIsValid(false);
    setFileValidationError(null);
    resetUpload();
    resetCreateMultipartUploadMutation();
    resetCompleteMultipartUploadMutation();
  }

  const uploadFile = useCallback(() => {
    if (!file) {
      return;
    }

    upload(file);
  }, [file]);

  useEffect(
    function validateFile() {
      if (!file) {
        return;
      }

      const reject = (problem: string) => {
        setFileIsValid(false);
        setFileValidationError(problem);
        raiseToast();
      };

      if (isZip) {
        // ATAD-136: a ZIP file may have no header, so parse rows as arrays.
        // Only a preview is checked here; the server reads the whole file
        // and reports what it loaded and skipped.
        if (file.size > MAX_ZIP_FILE_BYTES) {
          reject(`ZIP code files must be 10 MB or smaller. ${FILE_FORMAT_HINT}`);
          return;
        }

        Papa.parse(file, {
          header: false,
          skipEmptyLines: true,
          preview: 50,
          complete: (results) => {
            const problem = validateZipPreview(results.data as unknown[][]);

            if (problem) {
              reject(`${problem} ${FILE_FORMAT_HINT}`);
              return;
            }

            setFileIsValid(true);
            setFileValidationError(null);
            dismissToast();
          },
          error: (err) => {
            console.error(`Failed to read ${file.name}: ${err.message}`);
            reject(`We couldn't read this file. ${FILE_FORMAT_HINT}`);
          },
        });
        return;
      }

      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        preview: 5,
        complete: (results) => {
          // ATAD-135: this used to render `results.errors[0].message`, so a
          // user who uploaded a list of zip codes was shown Papa Parse's
          // "Unable to auto-detect delimiting character; defaulted to ','".
          // That warning fires on any single-column file and says nothing
          // about what to fix; worse, reporting it skipped the header check
          // that would have named the offending column.
          const problem = validateParsedFile(results, { zipUploadAvailable });

          if (problem) {
            // The raw diagnostics still belong somewhere — just not in front
            // of the person choosing a file.
            if (results.errors.length) {
              console.error('CSV parse errors:', results.errors);
            }

            // Clearing the flag matters when a second file is dropped over a
            // first, valid one: without it the stale `true` leaves Continue
            // wired up and an invalid file can still be uploaded.
            setFileIsValid(false);
            setFileValidationError(problem);
            raiseToast();
            return;
          }

          setFileIsValid(true);
          setFileValidationError(null);
          // A good file answers whatever the last one was told off for.
          dismissToast();
        },
        error: (err) => {
          console.error(`Failed to read ${file.name}: ${err.message}`);
          setFileIsValid(false);

          const message = `We couldn't read this file. ${FILE_FORMAT_HINT}`;
          setFileValidationError(message);
          raiseToast();
        },
      });
    },
    // Both toast helpers are stable, and a change of kind remounts this
    // component, so this still runs once per chosen file.
    [file, raiseToast, dismissToast, isZip, zipUploadAvailable]
  );

  useEffect(
    function bringErrorIntoView() {
      // The error text sits under the instructions and can be below the fold
      // of the modal's scrolling body — which is where the toast sends people.
      // Optional call: jsdom has no scrollIntoView.
      if (errorMessage) {
        errorTextRef.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
      }
    },
    [errorMessage]
  );

  const dropzoneClassname = useMemo(() => {
    const baseClassName =
      'p-12 border-2 border-dashed rounded-lg cursor-pointer bg-gray-50 transition-colors';
    return `${baseClassName} ${isDragActive ? 'border-[#14b8a6] bg-green-50' : 'border-gray-300 hover:border-gray-400'}`;
  }, [isDragActive]);

  const [dropzoneIcon, dropzoneIconColor] = useMemo(() => {
    // A file that failed validation used to keep the green tick, because only
    // dropzone-level rejections were considered here. Combined with the error
    // text sitting below the fold, the modal read as "all good" when it
    // wasn't. `bg-error` rather than `bg-secondary`: secondary is a brand
    // accent used for perfectly happy things elsewhere (the current-plan
    // badge), while `error` is the colour of the message itself.
    if (isDragReject || errorMessage) {
      return [rejectedIcon, 'bg-error'];
    }

    if (isDragAccept || file) {
      return [acceptedIcon, 'bg-primary'];
    }

    return [uploadIcon, 'bg-primary'];
  }, [file, errorMessage, isDragReject, isDragAccept]);

  useEffect(() => {
    if (file && fileIsValid) {
      dispatchAction({
        type: SET_ON_CONTINUE,
        payload: {
          onContinue: () => uploadFile(),
        },
      });
    }
  }, [dispatchAction, file, uploadFile, fileIsValid]);

  useEffect(() => {
    if (uploading !== undefined) {
      dispatchAction({
        type: SET_LOADING,
        payload: { loading: uploading },
      });
    }
  }, [dispatchAction, uploading]);

  useEffect(
    function handleUploadCompleted() {
      if (
        uploadDone &&
        !error &&
        !createMultipartUploadError &&
        !completeMultipartUploadError
      ) {
        dispatchAction({
          type: SET_SUCCESS,
          payload: {
            message: isZip
              ? zipUploadSuccessMessage(uploadedFileRef.current)
              : 'File uploaded successfully',
          },
        });
        apolloClient.refetchQueries({
          include: [FILES_QUERY],
        });
      }
    },
    [
      uploadDone,
      error,
      createMultipartUploadError,
      completeMultipartUploadError,
      apolloClient,
    ]
  );

  const [showPrompt, confirmNavigation, cancelNavigation] =
    useNavigationPrompt(uploading);

  return (
    <>
      <div className="w-full flex flex-col gap-6">
        <div className="flex flex-col justify-center items-center text-center w-full">
          <div className="w-full max-w-2xl">
            <div
              {...getRootProps({
                className: dropzoneClassname,
              })}
            >
              <input {...getInputProps()} />
              <div className="flex flex-col items-center">
                {uploading && <LoadingSpinner />}
                {!uploading && (
                  <div
                    className={`${dropzoneIconColor} w-20 h-20 rounded-full p-5 mb-6`}
                  >
                    <img
                      className="w-full h-full"
                      src={dropzoneIcon}
                      alt="upload icon"
                    />
                  </div>
                )}
                <div className="text-lg font-medium text-gray-700 mb-2">
                  {isDragActive ? 'Drop the file here...' : 'Drag and drop your file here'}
                </div>
                <p className="text-sm text-gray-500 mb-4">or</p>
                <button
                  type="button"
                  className="bg-[#14b8a6] text-white font-medium px-6 py-3 rounded-md hover:bg-teal-600 transition-colors text-sm"
                  onClick={(e) => {
                    // Prevent the click from bubbling to the dropzone root,
                    // whose onClick would call open() a second time and make
                    // the native file dialog appear twice.
                    e.stopPropagation();
                    if (file) {
                      handleClearFile();
                    } else {
                      open();
                    }
                  }}
                >
                  {file ? 'Clear file' : 'Browse Files'}
                </button>
                {file && (
                  <p className="text-sm text-gray-600 mt-3">
                    {file.name}
                    {file.size ? ` (${formatBytes(file.size)})` : ''}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
        {uploading && (
          // Held open for the whole upload, not just once progress crosses
          // zero, so the bar appearing doesn't shove the page down.
          <div className="mx-auto w-full max-w-2xl min-h-[56px]">
            {progress > 0 && (
              <div className="flex flex-col gap-2">
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div
                    className="h-3 bg-teal-500 transition-all duration-300 rounded-full"
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <p className="text-sm text-gray-600 text-center">
                  <span className="mr-1">Upload progress:</span>
                  {progress}%
                </p>
              </div>
            )}
          </div>
        )}
        <div className="flex flex-col justify-center items-center text-center mb-6">
          <CollapsibleInstructions kind={kind} />
          
          {errorMessage && (
            <div ref={errorTextRef}>
              <ErrorText className="mt-4" message={errorMessage} />
            </div>
          )}
        </div>
      </div>
      <ToastNotification
        message={UPLOAD_ERROR_TOAST}
        error={true}
        show={toastShown}
        duration={ERROR_TOAST_MS}
        onDismiss={dismissToast}
        position="top-center"
      />
      {showPrompt && (
        <NavigationPromptModal
          show={showPrompt}
          onConfirm={confirmNavigation}
          onCancel={cancelNavigation}
          cancelLabel="Stay here"
          directions={
            <>
              <p>Your file hasn't finished uploading.</p>
              <p>If you navigate away your upload will fail.</p>
              <p>
                You can continue working in a{' '}
                <a href={window.location.href} target="_blank">
                  new tab
                </a>
                .
              </p>
            </>
          }
        />
      )}
    </>
  );
}